import {
    useEffect,
    useState,
    type FC
} from "react";
import {
    type TextInputContentSizeChangeEvent,
    type TextInputKeyPressEvent,
    TextInput as NativeTextInput,
    type FocusEvent,
    type BlurEvent,
    Platform
} from "react-native";
import type IInputGroupTextAreaProps from "./type";
import type {
    TextAreaKeyPressEventData
} from "./type";
import stylesheet, {
    getInputGroupTextAreaIsSubmit,
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    usePointerType
} from "../../../../helpers/pointer";
import {
    useInputGroupContext
} from "../../context";
import {
    getIsFieldSizingSupported
} from "../../../textAreaInput/stylesheet";
import {
    getInputGroupDescription
} from "../../stylesheet";

const isAutoGrowFallback = Platform.OS === "web" && !getIsFieldSizingSupported();

const InputGroupTextArea: FC<IInputGroupTextAreaProps> = ({
    onContentSizeChange: onContentSizeChangeProp,
    onChangeText: onChangeTextProp,
    submitBehaviour = "modEnter",
    onKeyPress: onKeyPressProp,
    onFocus: onFocusProp,
    onBlur: onBlurProp,
    value: valueProp,
    defaultValue,
    maxRows = 8,
    minRows = 2,
    onSubmit,
    style,
    ...props
}) => {
    const {
        inputAccessibilityProps,
        placeholderTextColor,
        onInputLengthChange,
        isCounterOver,
        onInputFocus,
        counterText,
        currentSize,
        customTheme,
        onInputBlur,
        inputStyle,
        isDisabled,
        isReadOnly,
        counterID,
        isLoading,
        textHints,
        inputRef
    } = useInputGroupContext();

    const {
        typography,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const [
        value,
        setValue
    ] = useState(defaultValue ?? "");

    const [
        contentHeight,
        setContentHeight
    ] = useState<number | undefined>(undefined);

    const currentValue = valueProp ?? value;

    const pointerType = usePointerType();

    const {
        input: inputDynamicStyle
    } = useStyles({
        contentHeight: isAutoGrowFallback ? contentHeight : undefined,
        currentSize,
        pointerType,
        typography,
        maxRows,
        minRows,
        spaces
    });

    useEffect(() => {
        onInputLengthChange(currentValue.length);

        if(currentValue === "") {
            setContentHeight(undefined);
        }
    }, [
        currentValue
    ]);

    const onChangeText = (text: string) => {
        setValue(text);

        if(onChangeTextProp) {
            onChangeTextProp(text);
        }
    };

    const onContentSizeChange = (event: TextInputContentSizeChangeEvent) => {
        setContentHeight(event.nativeEvent.contentSize.height);

        if(onContentSizeChangeProp) {
            onContentSizeChangeProp(event);
        }
    };

    const onKeyPress = (event: TextInputKeyPressEvent) => {
        if(onKeyPressProp) {
            onKeyPressProp(event);
        }

        if(Platform.OS !== "web" || !onSubmit) {
            return;
        }

        const isSubmit = getInputGroupTextAreaIsSubmit({
            event: event.nativeEvent as TextAreaKeyPressEventData,
            submitBehaviour
        });

        if(!isSubmit) {
            return;
        }

        event.preventDefault();

        if(isDisabled || isReadOnly || isCounterOver || currentValue.trim() === "") {
            return;
        }

        onSubmit(currentValue);
    };

    const onFocus = (event: FocusEvent) => {
        onInputFocus();

        if(onFocusProp) {
            onFocusProp(event);
        }
    };

    const onBlur = (event: BlurEvent) => {
        onInputBlur();

        if(onBlurProp) {
            onBlurProp(event);
        }
    };

    return <NativeTextInput
        {...inputAccessibilityProps}
        {...props}
        {...getInputGroupDescription({
            counterID: counterText ? counterID : undefined,
            accessibilityProps: inputAccessibilityProps,
            describedBy: props["aria-describedby"],
            hint: props.accessibilityHint,
            textHints
        })}
        onContentSizeChange={isAutoGrowFallback ? onContentSizeChange : onContentSizeChangeProp}
        aria-busy={isLoading || inputAccessibilityProps["aria-busy"]}
        placeholderTextColor={placeholderTextColor}
        editable={!isDisabled && !isReadOnly}
        underlineColorAndroid="transparent"
        onChangeText={onChangeText}
        onKeyPress={onKeyPress}
        style={[
            style,
            stylesheet.input,
            inputStyle,
            inputDynamicStyle
        ]}
        value={currentValue}
        onFocus={onFocus}
        multiline={true}
        onBlur={onBlur}
        ref={inputRef}
    />;
};
export default InputGroupTextArea;
